import AutoResetEvent from './AutoResetEvent';
import Calibrator from './Calibrator';
import PressureOptimize from './PressureOptimize';
import SerialPortManager from './SerialPortManager';
import * as cv from '@techstark/opencv-js';
import FrameProcessorPressure from 'components/Modals/PatientModal/utils/FrameProcessorPressure';


export default class Scanner {

    bufferV4 = new Uint8Array(1000000);
    bufferV4SavePointer = 0;
    bufferV4readPointer = 0;
    running_flag = false;
    version4_ArraySize_x = 44;
    version4_ArraySize_y = 52;
    lastFrame = new cv.Mat();

    lastTick = Date.now(); // Store the initial timestamp;
    lastFrameRate = 0;
    frameRate = 0;
    isConnected = false;

    isRecording = false;
    recordDuration = 0.0;
    isSaveEnable = false;
    isArrayReady = false;

    savedFrames = [];
    savedFrameTimes = [];

    newTime_ms = 0;
    startTick = 0;

    isInitialized = false;
    isCanceled = false;

    constructor() {
        this.serialManager = new SerialPortManager();
        this.calibrator = new Calibrator();
        this.pressureOptemizer = new PressureOptimize();
        this.version = null;
        this._dataReceivedEvent = false;
        this._frameCapturedEvent = new AutoResetEvent();
        this.debug_mode = false;
        this.debug_str = "";
        this.isConnected = false;
        this.frameProcessPressure = new FrameProcessorPressure();
        this.isInitialized = false;
        this._onUnplugged = null;
        this._unplugHandled = false;
        this._serialWatchBound = false;
        this._onSerialDisconnect = (event) => {
            const port = event?.target;
            if (!this.serialManager.port) return;
            if (port && port !== this.serialManager.port) return;
            this.handleUnexpectedDisconnect();
        };
    }

    setOnUnplugged(callback) {
        this._onUnplugged = callback;
    }

    bindSerialWatch() {
        if (!("serial" in navigator) || this._serialWatchBound) return;
        navigator.serial.addEventListener("disconnect", this._onSerialDisconnect);
        this._serialWatchBound = true;
    }

    unbindSerialWatch() {
        if (!this._serialWatchBound || !("serial" in navigator)) return;
        navigator.serial.removeEventListener("disconnect", this._onSerialDisconnect);
        this._serialWatchBound = false;
    }

    handleUnexpectedDisconnect() {
        if (this._unplugHandled) return;
        if (!this.isConnected && !this.running_flag) return;
        this._unplugHandled = true;
        this.isConnected = false;
        const notify = this._onUnplugged;
        void this.Disconnect();
        try {
            notify?.();
        } catch {
            /* UI callback must not break disconnect */
        }
    }

    resetCaptureBuffers() {
        this.bufferV4 = new Uint8Array(1000000);
        this.bufferV4SavePointer = 0;
        this.bufferV4readPointer = 0;
        this._dataReceivedEvent = false;
        try {
            if (this.lastFrame && typeof this.lastFrame.isDeleted === "function" && !this.lastFrame.isDeleted()) {
                this.lastFrame.delete();
            }
        } catch {
            /* already released */
        }
        this.lastFrame = new cv.Mat();
    }

    async Connect() {
        this.isCanceled = false;
        this.resetCaptureBuffers();
        try {
            await this.findDevice({ prompt: false });
            let retries = 8;
            while (retries > 0 && !this.isConnected && !this.isCanceled) {
                retries--;
                await this.Delay(500);
                await this.findDevice({ prompt: false });
            }

            await this.Delay(300);

            if (!this.isConnected) {
                await this.Disconnect();
                throw new Error(
                    "Failed to connect to the pressure scanner. Click Connect scanner and select the USB device in Chrome. On Linux run: sudo usermod -aG dialout $USER then log out and back in. If it still fails: sudo systemctl stop brltty ModemManager"
                );
            }

            //await this.calibrator.Load();
            await this.pressureOptemizer.LoadSettings();

            // Send initialization commands with error handling
            await this.serialManager.writeWithTimeout(`se${this.calibrator.sensivity}`, 1000);
            await this.serialManager.readData();
            await this.serialManager.writeWithTimeout("StrgS", 1000);

            this._unplugHandled = false;
            this.bindSerialWatch();
            this.Run();

        } catch (error) {
            this.isConnected = false;
            await this.Disconnect();
            throw error;
        }
    }

    async Run() {
        this.running_flag = true;
        this.bufferV4USB();
        await this.Delay(2000);
        await this.MovingAverageInitial();
        this.SyncWorker_DoWork();
    }

    async bufferV4USB() {
        while (this.running_flag) {
            try {
                const dt = await this.serialManager.readDataByte();
                if (!dt) continue;
                if (!this.running_flag) return;
                var rdynum = dt.length;
                if (rdynum <= 0) continue;
                if (!(this.bufferV4 instanceof Uint8Array)) {
                    this.ClearBuffer();
                }
                if ((this.bufferV4SavePointer + rdynum) > this.bufferV4.length) {
                    this.bufferV4readPointer = 0;
                    this.bufferV4SavePointer = 0;
                }
                if (this.bufferV4SavePointer - this.bufferV4readPointer > 30) {
                    this._dataReceivedEvent = true;
                }
                if (this.bufferV4SavePointer - this.bufferV4readPointer < 0)
                    this.ClearBuffer();

                this.bufferV4.set(dt, this.bufferV4SavePointer);
                this.bufferV4SavePointer += rdynum;
            } catch (error) {
                if (this.running_flag && this.isConnected) {
                    this.handleUnexpectedDisconnect();
                }
                return;
            }
        }
    }

    async SyncWorker_DoWork() {
        while (this.running_flag) {
            try {
                // var start = new Date().getTime();                
                this.lastFrame = await this.CaptureFrame();
                // lock (LastFrame)

                if (this.isRecording) {
                    if (this.isSaveEnable) {
                        this.newTime_ms = (Date.now() - this.startTick)
                        if (this.newTime_ms > this.recordDuration) {
                            this.isArrayReady = true;
                            this.isRecording = false;
                            this.isSaveEnable = false;
                        }
                    }
                    else {
                        this.startTick = Date.now();
                        this.isSaveEnable = true;
                    }
                }

                if (this.isSaveEnable) {
                    this.savedFrames.push(this.lastFrame.clone());

                    this.newTime_ms = (Date.now() - this.startTick);
                    this.savedFrameTimes.push(this.newTime_ms);
                }
                this.CalculateFrameRate();

                await new Promise(resolve => setTimeout(resolve, 10)); // Prevents freezing

                // var end = new Date().getTime();
                // console.log(`timeeeee : ${end - start}`);
            } catch (error) {
                console.log(error);
                if (this.isConnected && this.running_flag) {
                    this.handleUnexpectedDisconnect();
                } else {
                    this.Disconnect();
                }
                return;
            }
        }
    }

    GetLastFrame() {
        try {
            if (!this.lastFrame || (typeof this.lastFrame.isDeleted === "function" && this.lastFrame.isDeleted()) || this.lastFrame.rows === 0) {
                return null;
            }
        } catch {
            return null;
        }
        let filterFrame = this.pressureOptemizer.Optimize(this.lastFrame.clone());
        let calibFrame = this.calibrator.calibrate(filterFrame.clone());

        filterFrame.delete();

        return calibFrame;
    }

    Delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async MovingAverageInitial() {
        let count = 10;
        for (let index = 0; index < count; index++) {
            await this.CaptureFrame();
        }
        let frameLocal = await this.CaptureFrame();
        let maxFrame = frameLocal.clone();

        for (let i = 0; i < 20; i++) {
            frameLocal = await this.CaptureFrame();
            frameLocal = this.pressureOptemizer.Optimize(frameLocal);
            if (frameLocal !== undefined) {
                cv.max(maxFrame, frameLocal, maxFrame);
            }
        }
        if (maxFrame === null) throw new Error("Some Error Occured in Capturing Zero Frame");

        this.calibrator.zeroFrame = maxFrame.clone();
        this.isInitialized = true;

        frameLocal.delete();
        maxFrame.delete();
    }

    async CaptureFrame() {
        const currentPacket = await this.GetLastRawDataV4();
        let frame = this.ExtractIMGfromStr(currentPacket);
        let frameRotated = this.RotateFrameSensorV4(frame);
        frame.delete();
        return frameRotated;
    }

    ExtractIMGfromStr(Packet) {
        let numel = Packet[5] * 256 + Packet[6] - 7;
        let m = 0, j;
        // let Values = Array.from({ length: this.version4_ArraySize_x }, () => new Array(this.version4_ArraySize_y).fill(0));
        let Values = new cv.Mat(this.version4_ArraySize_x, this.version4_ArraySize_y, cv.CV_16U);
        let extracted_data = new Uint8Array(12501);
        let loopTo = numel - 1;

        for (var i = 7; i <= loopTo + 7; i++) {
            if (Packet[i] === 0 && i !== loopTo + 7) {
                var loopTo1 = Packet[i + 1];
                for (j = 0; j < loopTo1; j++) {
                    extracted_data[m] = 0;
                    m = m + 1;
                }
                i++;
            }
            else {
                extracted_data[m] = Packet[i];
                m++;
            }
        }
        m = 0;
        for (var c = 0; c < this.version4_ArraySize_y; c++) {
            for (var r = 0; r < this.version4_ArraySize_x; r++) {
                // Values[r][c] = extracted_data[m] * 16;
                Values.ushortPtr(r, c)[0] = extracted_data[m] * 16;
                m++;
            }
        }
        //var frameMat = this.CreateGrayscaleImage(Values);

        return Values;
    }

    waitForReady() {
        return new Promise((resolve) => {
            const check = () => {
                if (this._dataReceivedEvent) {
                    resolve("The flag is now true!");
                } else {
                    setTimeout(check, 10); // Check again 
                }
            };
            check();
        });
    }

    async GetLastRawDataV4() {
        let framebyte = new Uint8Array(5);
        framebyte = [70, 114, 97, 109, 101];
        let startidx = 0;
        let stopidx = 0;
        let startframeidx = -1, k = 0;
        let tmp = new Uint8Array();

        do {
            await this.waitForReady();
            startidx = this.bufferV4readPointer;
            stopidx = this.bufferV4SavePointer;

            if (stopidx - startidx > 23000)             // maximum frame buffer size = 48 * 240 * 2
                stopidx = startidx + 23000;

            this._dataReceivedEvent = false;

            tmp = new Uint8Array(stopidx - startidx);
            k = 0;
            for (var i = startidx; i < stopidx; i++) {
                tmp[k] = this.bufferV4[i];
                k++;
            }
            startframeidx = this.SearchBytes(tmp, framebyte);

        } while (startframeidx === -1);
        var numel = tmp[5 + startframeidx] * 256 + tmp[5 + 1 + startframeidx];
        let output = new Uint8Array(numel);

        if (tmp.length < startframeidx + numel)
            return output;

        k = 0;
        for (i = startframeidx; i < startframeidx + numel; i++) {
            output[k] = tmp[i];
            k++;
        }

        if (this.pressureOptemizer.SkipFrames)
            this.bufferV4readPointer = stopidx;
        else
            this.bufferV4readPointer = startidx + startframeidx + numel;

        return output;
    }

    SearchBytes(haystack, needle) {
        let len = needle.length;
        let limit = haystack.length - len;

        for (var i = 0; i <= limit; i++) {
            let k = 0;
            for (; k < len; k++) {
                if (needle[k] !== haystack[i + k]) break;
            }
            if (k === len) return i;
        }
        return -1;
    }

    RotateFrameSensorV4(frame) {
        let frameRotated = new cv.Mat(frame.cols, frame.rows, frame.type());
        let dsize = new cv.Size(frame.rows, frame.cols);
        let center = new cv.Point(frame.cols / 2, frame.rows / 2);
        let M = cv.getRotationMatrix2D(center, 90, 1);
        cv.warpAffine(frame, frameRotated, M, dsize, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());
        M.delete();
        return frameRotated;
    }

    CalculateFrameRate() {
        let currentTick = Date.now(); // Get current timestamp
        if (currentTick - this.lastTick >= 1000) { // Check if 1 second has passed
            this.lastFrameRate = this.frameRate;
            this.frameRate = 0;
            this.lastTick = currentTick;
        }

        this.frameRate++;
    }

    ClearBuffer() {
        this.bufferV4 = new Uint8Array(1000000);
        this.bufferV4SavePointer = 0;
        this.bufferV4readPointer = 0;
    }

    async Disconnect() {
        this.running_flag = false;
        this.isCanceled = true;
        this.unbindSerialWatch();
        this.ClearBuffer();

        try {
            if (this.serialManager.isOpen()) {
                await this.serialManager.writeWithTimeout("StrgE", 1000);
            }
            await this.serialManager.closePort();

            this.savedFrames.forEach((frame) => {
                try {
                    if (frame && typeof frame.isDeleted === "function" && !frame.isDeleted()) {
                        frame.delete();
                    }
                } catch {
                    /* already released */
                }
            });
            this.savedFrames = [];
            try {
                if (this.lastFrame && typeof this.lastFrame.isDeleted === "function" && !this.lastFrame.isDeleted()) {
                    this.lastFrame.delete();
                }
            } catch {
                /* already released */
            }
            this.lastFrame = new cv.Mat();
        } catch (error) {
        }
    }

    async pickPort() {
        return this.serialManager.requestPort();
    }

    async identifyScanner() {
        await this.serialManager.openPort({ baudRate: 115200 });
        const replies = [];
        for (const command of ["Whois", "Whois\r\n"]) {
            await this.serialManager.writeWithTimeout(command, 1000);
            let result = String(await this.serialManager.readWithTimeout(2000) || "").trim();
            if (result.includes("Frame")) {
                result = String(await this.serialManager.readWithTimeout(2000) || "").trim();
            }
            if (result) replies.push(result);
            if (result.startsWith("Model: PT-HFScan")) {
                this.isConnected = true;
                return true;
            }
        }
        if (replies.length) {
            console.warn("Pressure scanner handshake reply:", replies.join(" | "));
        }
        return false;
    }

    async findDevice({ prompt = false } = {}) {
        if (this.isConnected) return;
        const ports = await this.serialManager.getSerialPorts();

        for (const port of ports) {
            this.serialManager.setPort(port);
            try {
                if (await this.identifyScanner()) return;
            } catch (error) {
                console.error(`Error connecting to ${port}: ${error}`);
            }
            await this.serialManager.closePort();
        }

        if (!prompt) return;

        const port = await this.serialManager.requestPort();
        if (!port) return;
        try {
            if (await this.identifyScanner()) return;
        } catch (error) {
            console.error(`Error connecting to ${port}: ${error}`);
        }
        await this.serialManager.closePort();
    };

    stopRecord() {
        this.isArrayReady = true;
        this.isRecording = false;
        this.isSaveEnable = false;
    }

    record(second) {
        this.newTime_ms = 0;
        if (!this.savedFrames) {
            this.savedFrames = [];
        }
        else {
            this.savedFrames.forEach(item => item.delete());
            this.savedFrames = [];
        }
        this.savedFrameTimes = [];
        this.isArrayReady = false;
        this.recordDuration = second * 1000.0;
        this.isRecording = true;
        this.isSaveEnable = false;
    }

    getElapsedms() {
        return this.newTime_ms;
    }

    getCopyOfSavedFrames() {
        let output = [];
        if (this.savedFrames.length > 0) {
            let trimEnds = this.frameProcessPressure.getTrimEnds(this.savedFrames, this.lastFrameRate / 4);

            for (let i = trimEnds.start; i < trimEnds.end; i++) {
                if (!this.savedFrames[i]) continue;
                let filterFrame = this.pressureOptemizer.Optimize(this.savedFrames[i].clone());
                output.push(this.calibrator.calibrate(filterFrame));
            }
        }
        return output;
    }

    getCopyOfSavedFramesTimes() {
        let output = [];
        for (let i = 0; i < this.savedFrameTimes.length; i++) {
            output.push(this.savedFrameTimes[i]);
        }
        return output;
    }

    getFrameRate() {
        return this.frameRate;
    }

    getSavedCount() {
        return this.savedFrames.length;
    }

    setCalibrator(calibrator){
        if(calibrator== null) return;
        this.calibrator = calibrator;
    }

    //-------------------------debug------------//
    async savecsv_debug() {
        this.debug_mode = true;
        await this.waitFordebug();
        return this.debug_str;
    }
    waitFordebug() {
        return new Promise((resolve) => {
            const check = () => {
                if (!this.debug_mode) {
                    resolve("debug mode");
                } else {
                    setTimeout(check, 50); // Check again 
                }
            };
            check();
        });
    }
    CreateGrayscaleImage(uintArray) {
        // const height = uintArray.length; // Rows
        // const width = uintArray[0].length; // Columns

        // let imgMat = new cv.Mat(height, width, cv.CV_8U);

        // for (let i = 0; i < height; i++) {
        //     for (let j = 0; j < width; j++) {
        //         imgMat.ucharPtr(i, j)[0] = uintArray[i][j]; // Set pixel value
        //     }
        // }

        //return imgMat;
        let str = '';
        if (this.debug_mode) {
            for (let i = 0; i < uintArray.rows; i++) {
                let line = '';
                for (let j = 0; j < uintArray.cols; j++) {
                    if (line !== '') line += ',';
                    line += uintArray.ushortPtr(i, j)[0];
                }
                str += line + '\r\n';
            }
            this.debug_mode = false;
        }
        this.debug_str = str;
        return uintArray;
    }

    //-------------------------end debug------------//



}